import ISensor from 'app/business-logic/domain-models/ISensor';
import IThreshold from 'app/business-logic/domain-models/IThreshold';
import { IMeasurement } from 'app/business-logic/domain-models/Monitoring';

export interface IRange {
  min: number;
  max?: number;
}

export interface IThresholdBand {
  index: number;
  min: number;
  max: number;
  color?: string;
}

export const THRESHOLD_BASE_VALUE = -3.4e38;
export const THRESHOLD_NO_DATA_COLOR = '#C1C6C9';
export const THRESHOLD_DEFAULT_COLOR = '#679B38';

const DEFAULT_THRESHOLD_BAND = {
  index: 0,
  min: Number.MIN_VALUE,
  max: Number.MAX_VALUE,
  color: THRESHOLD_DEFAULT_COLOR,
};

/**
 * Calculates the thresholds bounds for the given value.
 */
export const calculateThresholdBand = (
  sensor: ISensor,
  measurement: IMeasurement | undefined,
  ignoreStaleValues: boolean
): IThresholdBand | null => {
  //if we ignore stale values, then set the value to null if it's stale
  const value = measurement && (ignoreStaleValues && measurement.isStale ? null : measurement.value);

  if (value === null || value === undefined) {
    return {
      min: THRESHOLD_BASE_VALUE,
      max: THRESHOLD_BASE_VALUE,
      color: THRESHOLD_NO_DATA_COLOR,
    } as IThresholdBand;
  }

  if (!(sensor.thresholds && sensor.thresholds.length)) {
    return DEFAULT_THRESHOLD_BAND;
  }

  const { thresholds } = sensor;

  let thresholdIndex = thresholds
    .filter(({ max }) => typeof max === 'number')
    .findIndex(({ max }) => (max as number) >= value);
  if (thresholdIndex < 0) {
    thresholdIndex = thresholds.length - 1;
    // Exceeded maximum threshold
    const threshold = thresholds[thresholdIndex];
    return {
      index: thresholdIndex,
      min: threshold?.max as number,
      max: value,
      color: threshold?.color,
    };
  }

  const { max } = thresholds[thresholdIndex] as IThreshold;

  let min: number;
  let color: string | undefined = undefined;

  if (thresholdIndex === 0) {
    min = 0;
  } else {
    thresholdIndex = thresholdIndex - 1;
    const minThreshold = thresholds[thresholdIndex];

    color = minThreshold?.color;
    min = minThreshold?.max as number;

    // Special case scenario that effectively represents "no threshold" -
    // for now we consider all thresholds to be "0 => max value".
    if (min <= THRESHOLD_BASE_VALUE) {
      min = THRESHOLD_BASE_VALUE;
    }
  }

  return {
    index: thresholdIndex,
    min,
    max: max as number,
    color,
  };
};

/**
 * Gets the range of values that are covered by thresholds.
 */
export const getThresholdBounds = (thresholds: IThreshold[]): IRange => {
  if (!(thresholds && thresholds.length)) {
    return { min: 0, max: Infinity };
  }

  const maxThreshold = thresholds[thresholds.length - 1];

  // For now we consider all thresholds to be "0 => max value"
  return {
    min: 0,
    max: maxThreshold?.max,
  };
};

export const calculateHeatMapIntensity = (value: number, thresholds: IThreshold[]): number | null => {
  if (!(value && thresholds && thresholds.length)) {
    return null;
  }

  const bounds = getThresholdBounds(thresholds);
  if (typeof bounds.max === 'number' && value > bounds.max) {
    return 1;
  }

  let intensity = 0;
  let thresholdMin = bounds.min;

  const thresholdIntensityWeight = 1 / thresholds.length;

  for (const threshold of thresholds) {
    const { max: thresholdMax } = threshold;

    if (typeof thresholdMax === 'number' && value >= thresholdMax) {
      intensity += thresholdIntensityWeight;
      thresholdMin = Math.max(thresholdMin, thresholdMax);
    } else {
      if (typeof thresholdMax !== 'number') return null;
      const percentageThroughThreshold = (value - thresholdMin) / (thresholdMax - thresholdMin);
      return intensity + percentageThroughThreshold * thresholdIntensityWeight;
    }
  }

  return null;
};
